/**
 * YUMIX — Sound Director (Iter. 390).
 *
 * Synthesized milestone feedback via Web Audio API. ZERO audio files —
 * everything is generated at runtime with oscillators + ADSR envelopes,
 * which means:
 *   - works offline,
 *   - instant (no preload, no buffer),
 *   - tiny bundle (no .mp3/.wav assets),
 *   - distinctive YUMIX sound signature (recognisable across the app).
 *
 * Each event has a unique "voice" — frequency arpeggio, waveform, envelope —
 * tuned to feel rewarding (rising arpeggios on wins, soft chimes on logs,
 * deep bass kicks on alerts).
 *
 * Usage:
 *   import { play } from '../lib/soundDirector';
 *   play('badge_unlocked');
 *
 * Or via the React hook:
 *   const playSound = useSound();
 *   playSound('streak_up');
 *
 * Respects:
 *   - `localStorage["yumix_sounds_enabled"]` master toggle (default ON)
 *   - `prefers-reduced-motion: reduce` (auto-mute, accessibility)
 *   - Volume range 0.0 → 1.0 via setVolume()
 */

// ── State ────────────────────────────────────────────────────────────
const STATE = {
  ctx: null,
  master: null,
  enabled: true,
  volume: 0.35,
  pack: 'classic',
};

const STORAGE_KEY = 'yumix_sounds_enabled';
const VOLUME_KEY = 'yumix_sounds_volume';
const PACK_KEY = 'yumix_sound_pack';

function loadPrefs() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (raw !== null) STATE.enabled = raw === 'true' || raw === '1';
    const v = parseFloat(localStorage.getItem(VOLUME_KEY) || '');
    if (!Number.isNaN(v) && v >= 0 && v <= 1) STATE.volume = v;
    const p = localStorage.getItem(PACK_KEY);
    if (p && typeof p === 'string') STATE.pack = p;
  } catch (_) { /* private mode */ }
}
loadPrefs();

export function isEnabled() { return STATE.enabled; }
export function isReducedMotion() {
  try {
    return typeof window !== 'undefined' &&
      window.matchMedia &&
      window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  } catch (_) { return false; }
}
export function setEnabled(on) {
  STATE.enabled = !!on;
  try { localStorage.setItem(STORAGE_KEY, on ? 'true' : 'false'); } catch (_) {}
}
export function getVolume() { return STATE.volume; }
export function setVolume(v) {
  const x = Math.max(0, Math.min(1, Number(v) || 0));
  STATE.volume = x;
  try { localStorage.setItem(VOLUME_KEY, String(x)); } catch (_) {}
  if (STATE.master) STATE.master.gain.value = x;
}

function ensureContext() {
  if (STATE.ctx) return STATE.ctx;
  try {
    const Ctx = window.AudioContext || window.webkitAudioContext;
    if (!Ctx) return null;
    STATE.ctx = new Ctx();
    STATE.master = STATE.ctx.createGain();
    STATE.master.gain.value = STATE.volume;
    STATE.master.connect(STATE.ctx.destination);
  } catch (_) { return null; }
  return STATE.ctx;
}

function envelope(ctx, gainNode, when, attack, decay, sustain, release, peak = 1.0) {
  const g = gainNode.gain;
  g.cancelScheduledValues(when);
  g.setValueAtTime(0.0001, when);
  g.exponentialRampToValueAtTime(peak,            when + attack);
  g.exponentialRampToValueAtTime(peak * sustain,  when + attack + decay);
  g.exponentialRampToValueAtTime(0.0001,          when + attack + decay + release);
}

function tone(ctx, freq, when, dur, type = 'sine', peak = 0.6) {
  const osc = ctx.createOscillator();
  const gain = ctx.createGain();
  osc.type = type;
  osc.frequency.value = freq;
  osc.connect(gain);
  gain.connect(STATE.master);
  envelope(ctx, gain, when, 0.005, 0.04, 0.6, dur, peak);
  osc.start(when);
  osc.stop(when + dur + 0.05);
}

function noiseHit(ctx, when, dur = 0.12, peak = 0.4) {
  // Synthesize a short noise burst by routing a buffer through a bandpass
  const bufSize = Math.floor(ctx.sampleRate * dur);
  const buf = ctx.createBuffer(1, bufSize, ctx.sampleRate);
  const data = buf.getChannelData(0);
  for (let i = 0; i < bufSize; i++) data[i] = Math.random() * 2 - 1;
  const src = ctx.createBufferSource();
  src.buffer = buf;
  const bp = ctx.createBiquadFilter();
  bp.type = 'bandpass';
  bp.frequency.value = 4000;
  bp.Q.value = 0.8;
  const gain = ctx.createGain();
  src.connect(bp); bp.connect(gain); gain.connect(STATE.master);
  envelope(ctx, gain, when, 0.001, 0.02, 0.4, dur, peak);
  src.start(when);
  src.stop(when + dur + 0.05);
}

// ── Voices ───────────────────────────────────────────────────────────
// Each event = a tiny composition. Frequencies in Hz.
// Each PACK is a complete VOICES override — sound packs are unlocked via
// gamification (longest_streak) and validated server-side. See SOUND_PACKS_CATALOG.
const PACK_CLASSIC = {
  // 🏆 Badge unlocked — 4-note rising major arpeggio + sparkle noise.
  badge_unlocked: (ctx, t0) => {
    const notes = [523.25, 659.25, 783.99, 1046.50]; // C5 E5 G5 C6
    notes.forEach((f, i) => tone(ctx, f, t0 + i * 0.09, 0.18, 'triangle', 0.55));
    noiseHit(ctx, t0 + 0.3, 0.18, 0.18);
  },
  // 🔥 Streak +1 — tight 2-note ascending pop, satisfying.
  streak_up: (ctx, t0) => {
    tone(ctx, 698.46, t0,        0.10, 'square',   0.32); // F5
    tone(ctx, 1046.5, t0 + 0.08, 0.14, 'triangle', 0.45); // C6
  },
  // 🍽️ Meal logged — soft round wood-block tap + low resonant body.
  meal_logged: (ctx, t0) => {
    tone(ctx, 880,    t0, 0.08,  'sine', 0.30); // A5
    tone(ctx, 440,    t0, 0.20,  'sine', 0.20); // A4 (resonant)
  },
  // 💧 Water glass — quick dewdrop blip rising.
  water_glass: (ctx, t0) => {
    const osc = ctx.createOscillator();
    const gain = ctx.createGain();
    osc.type = 'sine';
    osc.frequency.setValueAtTime(900, t0);
    osc.frequency.exponentialRampToValueAtTime(1500, t0 + 0.12);
    osc.connect(gain); gain.connect(STATE.master);
    envelope(ctx, gain, t0, 0.005, 0.04, 0.5, 0.12, 0.40);
    osc.start(t0); osc.stop(t0 + 0.18);
  },
  // 🎯 Deficit hit — confident bass + mid chime, 3 notes.
  deficit_hit: (ctx, t0) => {
    tone(ctx, 196,    t0,        0.25, 'sine',     0.35); // G3 bass
    tone(ctx, 587.33, t0 + 0.05, 0.18, 'triangle', 0.40); // D5
    tone(ctx, 880,    t0 + 0.13, 0.20, 'triangle', 0.35); // A5
  },
  // ⏰ Fasting milestone — slow rising sine glide, meditative.
  fasting_milestone: (ctx, t0) => {
    const osc = ctx.createOscillator();
    const gain = ctx.createGain();
    osc.type = 'sine';
    osc.frequency.setValueAtTime(330, t0);
    osc.frequency.exponentialRampToValueAtTime(660, t0 + 0.5);
    osc.connect(gain); gain.connect(STATE.master);
    envelope(ctx, gain, t0, 0.04, 0.1, 0.5, 0.5, 0.30);
    osc.start(t0); osc.stop(t0 + 0.7);
  },
  // ❌ Light error — short low descending plonk (NOT shrill).
  soft_error: (ctx, t0) => {
    const osc = ctx.createOscillator();
    const gain = ctx.createGain();
    osc.type = 'square';
    osc.frequency.setValueAtTime(220, t0);
    osc.frequency.exponentialRampToValueAtTime(140, t0 + 0.10);
    osc.connect(gain); gain.connect(STATE.master);
    envelope(ctx, gain, t0, 0.005, 0.03, 0.4, 0.10, 0.28);
    osc.start(t0); osc.stop(t0 + 0.18);
  },
  // 🔔 Generic tap — used for previewing volume from Settings.
  tap: (ctx, t0) => {
    tone(ctx, 880, t0, 0.06, 'triangle', 0.35);
  },
};

// 🧘 ZEN — soft sine waves, calm breath-like envelopes, low frequencies.
// Designed for users who want feedback without dopamine spike.
const PACK_ZEN = {
  badge_unlocked: (ctx, t0) => {
    // Soft 3-note pentatonic chime (no sparkle).
    tone(ctx, 392,    t0,        0.55, 'sine', 0.28); // G4
    tone(ctx, 523.25, t0 + 0.18, 0.55, 'sine', 0.24); // C5
    tone(ctx, 659.25, t0 + 0.36, 0.65, 'sine', 0.22); // E5
  },
  streak_up: (ctx, t0) => {
    // Single warm "om" tone, no rise.
    tone(ctx, 440, t0, 0.30, 'sine', 0.26);
  },
  meal_logged: (ctx, t0) => {
    // Faint wood-bowl tap.
    tone(ctx, 330, t0, 0.45, 'sine', 0.22);
  },
  water_glass: (ctx, t0) => {
    // Slow dewdrop (longer decay than classic).
    const osc = ctx.createOscillator();
    const gain = ctx.createGain();
    osc.type = 'sine';
    osc.frequency.setValueAtTime(660, t0);
    osc.frequency.exponentialRampToValueAtTime(990, t0 + 0.35);
    osc.connect(gain); gain.connect(STATE.master);
    envelope(ctx, gain, t0, 0.02, 0.12, 0.5, 0.40, 0.28);
    osc.start(t0); osc.stop(t0 + 0.55);
  },
  deficit_hit: (ctx, t0) => {
    // Deep meditative drone + soft mid bell.
    tone(ctx, 164.81, t0,        0.70, 'sine', 0.24); // E3 drone
    tone(ctx, 523.25, t0 + 0.20, 0.50, 'sine', 0.22); // C5
  },
  fasting_milestone: (ctx, t0) => {
    // Long meditative bowl with gentle pitch glide.
    const osc = ctx.createOscillator();
    const gain = ctx.createGain();
    osc.type = 'sine';
    osc.frequency.setValueAtTime(220, t0);
    osc.frequency.exponentialRampToValueAtTime(330, t0 + 1.0);
    osc.connect(gain); gain.connect(STATE.master);
    envelope(ctx, gain, t0, 0.08, 0.2, 0.6, 1.0, 0.26);
    osc.start(t0); osc.stop(t0 + 1.3);
  },
  soft_error: (ctx, t0) => {
    tone(ctx, 196, t0, 0.40, 'sine', 0.22);
  },
  tap: (ctx, t0) => {
    tone(ctx, 660, t0, 0.18, 'sine', 0.22);
  },
};

// 🌈 NÉON — FM-bells with brief delay/echo, brilliant highs, retro-futuristic.
// Unlocked at longest_streak >= 7. Uses 2-oscillator FM-like modulation for shimmer.
function fmBell(ctx, freq, ratio, modIdx, when, dur, peak = 0.4) {
  const carrier = ctx.createOscillator();
  const modulator = ctx.createOscillator();
  const modGain = ctx.createGain();
  const gain = ctx.createGain();
  carrier.type = 'sine';
  modulator.type = 'sine';
  carrier.frequency.value = freq;
  modulator.frequency.value = freq * ratio;
  modGain.gain.value = freq * modIdx;
  modulator.connect(modGain); modGain.connect(carrier.frequency);
  carrier.connect(gain); gain.connect(STATE.master);
  envelope(ctx, gain, when, 0.003, 0.05, 0.4, dur, peak);
  modulator.start(when); carrier.start(when);
  modulator.stop(when + dur + 0.1); carrier.stop(when + dur + 0.1);
}
const PACK_NEON = {
  badge_unlocked: (ctx, t0) => {
    // 4-note shimmering arpeggio + echo.
    const notes = [659.25, 880, 1174.66, 1567.98]; // E5 A5 D6 G6
    notes.forEach((f, i) => {
      fmBell(ctx, f, 2.01, 1.2, t0 + i * 0.08, 0.30, 0.40);
      fmBell(ctx, f, 2.01, 0.8, t0 + i * 0.08 + 0.18, 0.20, 0.18); // echo
    });
  },
  streak_up: (ctx, t0) => {
    fmBell(ctx, 880,    2.0, 1.4, t0,        0.18, 0.40);
    fmBell(ctx, 1318.5, 2.0, 1.4, t0 + 0.10, 0.22, 0.38);
  },
  meal_logged: (ctx, t0) => {
    fmBell(ctx, 698.46, 3.01, 1.0, t0, 0.22, 0.32);
  },
  water_glass: (ctx, t0) => {
    fmBell(ctx, 1046.5, 2.5, 1.6, t0, 0.20, 0.34);
    fmBell(ctx, 1318.5, 2.5, 1.6, t0 + 0.06, 0.18, 0.28);
  },
  deficit_hit: (ctx, t0) => {
    fmBell(ctx, 196,    1.5, 0.8, t0,        0.40, 0.30); // bass FM
    fmBell(ctx, 783.99, 2.0, 1.4, t0 + 0.08, 0.28, 0.40);
    fmBell(ctx, 1174.66, 2.0, 1.4, t0 + 0.18, 0.32, 0.36);
  },
  fasting_milestone: (ctx, t0) => {
    fmBell(ctx, 440, 1.5, 2.0, t0, 0.90, 0.30);
    fmBell(ctx, 660, 1.5, 2.0, t0 + 0.20, 0.70, 0.24);
  },
  soft_error: (ctx, t0) => {
    fmBell(ctx, 220, 2.5, 1.0, t0, 0.18, 0.28);
  },
  tap: (ctx, t0) => {
    fmBell(ctx, 1046.5, 2.0, 1.0, t0, 0.10, 0.30);
  },
};

// 👾 8-BIT — chiptune square waves with quick pitch bends and arpeggios.
// Unlocked at longest_streak >= 50. NES/SNES homage.
function bend(ctx, fStart, fEnd, when, dur, type = 'square', peak = 0.30) {
  const osc = ctx.createOscillator();
  const gain = ctx.createGain();
  osc.type = type;
  osc.frequency.setValueAtTime(fStart, when);
  osc.frequency.exponentialRampToValueAtTime(fEnd, when + dur);
  osc.connect(gain); gain.connect(STATE.master);
  envelope(ctx, gain, when, 0.001, 0.02, 0.7, dur, peak);
  osc.start(when); osc.stop(when + dur + 0.05);
}
const PACK_8BIT = {
  badge_unlocked: (ctx, t0) => {
    // Classic "level up" arpeggio (Mario-esque).
    const notes = [523.25, 659.25, 783.99, 1046.50, 1318.51];
    notes.forEach((f, i) => tone(ctx, f, t0 + i * 0.06, 0.08, 'square', 0.30));
  },
  streak_up: (ctx, t0) => {
    bend(ctx, 440, 880, t0, 0.10, 'square', 0.28);
  },
  meal_logged: (ctx, t0) => {
    tone(ctx, 659.25, t0,        0.06, 'square', 0.26);
    tone(ctx, 880,    t0 + 0.05, 0.08, 'square', 0.26);
  },
  water_glass: (ctx, t0) => {
    bend(ctx, 660, 1320, t0, 0.08, 'square', 0.28);
  },
  deficit_hit: (ctx, t0) => {
    // Coin pickup vibe.
    tone(ctx, 987.77, t0,        0.08, 'square', 0.28); // B5
    tone(ctx, 1318.51, t0 + 0.07, 0.16, 'square', 0.28); // E6
  },
  fasting_milestone: (ctx, t0) => {
    // Slow chiptune fanfare.
    const notes = [392, 523.25, 659.25, 783.99];
    notes.forEach((f, i) => tone(ctx, f, t0 + i * 0.12, 0.14, 'square', 0.26));
  },
  soft_error: (ctx, t0) => {
    bend(ctx, 220, 110, t0, 0.18, 'square', 0.26);
  },
  tap: (ctx, t0) => {
    tone(ctx, 1046.5, t0, 0.05, 'square', 0.26);
  },
};

const PACKS = {
  classic: PACK_CLASSIC,
  zen:     PACK_ZEN,
  neon:    PACK_NEON,
  '8bit':  PACK_8BIT,
};

export const SOUND_PACK_IDS = Object.keys(PACKS);

export function getActivePack() { return STATE.pack; }
export function setActivePack(packId) {
  if (!PACKS[packId]) return false;
  STATE.pack = packId;
  try { localStorage.setItem(PACK_KEY, packId); } catch (_) {}
  return true;
}

export function play(event) {
  if (!STATE.enabled) return;
  if (isReducedMotion()) return;
  const pack = PACKS[STATE.pack] || PACK_CLASSIC;
  const voice = pack[event] || pack.tap || PACK_CLASSIC.tap;
  const ctx = ensureContext();
  if (!ctx) return;
  // Resume on user gesture if the context is suspended (Chrome autoplay policy).
  if (ctx.state === 'suspended') {
    try { ctx.resume(); } catch (_) {}
  }
  try {
    voice(ctx, ctx.currentTime + 0.005);
  } catch (_) { /* swallow — sound is non-critical */ }
}

export const EVENTS = Object.keys(PACK_CLASSIC);
export default { play, isEnabled, setEnabled, getVolume, setVolume, getActivePack, setActivePack, EVENTS, SOUND_PACK_IDS };
